import { isNativeAndroid, isNativeIOS, isNativeSocialLoginBridgeAvailable } from './NativeUtil';
import {
  buildFacebookOAuthUrl,
  buildGoogleOAuthUrl,
  getKakaoCredentialWithBridgeFallback,
  getSocialCredentialFromWeb,
  requestNativeSocialLogin
} from './socialAuthWeb';
import type { SocialLoginCredentialInput } from '../data/repositories/SocialLogin';
import type { SocialProvider } from '../data/repositories/SocialLoginMethods';

export type SocialLoginHostRuntime = 'web' | 'ios' | 'android';

export function getSocialLoginHostRuntime(): SocialLoginHostRuntime {
  if (isNativeIOS()) return 'ios';
  if (isNativeAndroid()) return 'android';
  return 'web';
}

export type NativeOAuthRedirectResult =
  | { status: 'started' }
  | { status: 'skipped'; reason: 'no_bridge' | 'no_url' };

export function tryStartNativeOAuthRedirect(
  provider: 'google' | 'facebook',
  isPromotionAgreed = false
): NativeOAuthRedirectResult {
  if (!isNativeSocialLoginBridgeAvailable()) {
    return { status: 'skipped', reason: 'no_bridge' };
  }
  const authUrl =
    provider === 'google'
      ? buildGoogleOAuthUrl(isPromotionAgreed)
      : buildFacebookOAuthUrl(isPromotionAgreed);
  if (!authUrl) {
    return { status: 'skipped', reason: 'no_url' };
  }
  requestNativeSocialLogin(provider, authUrl);
  return { status: 'started' };
}

type PocapanSocialAuthBridge = {
  login?: (provider: string) => Promise<{
    idToken?: string;
    accessToken?: string;
    identityToken?: string;
    identity_token?: string;
  } | null>;
};

function getPocapanSocialAuthBridge(): PocapanSocialAuthBridge | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as Window & { PocapanSocialAuth?: PocapanSocialAuthBridge }).PocapanSocialAuth;
}

export async function getOnboardingSocialCredential(
  provider: SocialProvider
): Promise<SocialLoginCredentialInput | null> {
  if (provider === 'kakao') {
    return getKakaoOnboardingCredential();
  }

  if (provider === 'facebook') {
    return getSocialCredentialFromWeb('facebook');
  }

  const bridge = getPocapanSocialAuthBridge();
  if (bridge?.login) {
    return Promise.resolve(bridge.login(provider)).then((credential) => {
      if (!credential) {
        return null;
      }

      const idToken =
        provider === 'apple'
          ? (credential.idToken ?? credential.identityToken ?? credential.identity_token)
          : credential.idToken;

      return {
        idToken,
        accessToken: credential.accessToken
      };
    });
  }

  return getSocialCredentialFromWeb(provider);
}

async function getKakaoOnboardingCredential(): Promise<SocialLoginCredentialInput | null> {
  const cred = await getKakaoCredentialWithBridgeFallback();
  if (cred) return cred;

  const bridge = getPocapanSocialAuthBridge();
  if (bridge?.login) {
    return Promise.resolve(bridge.login('kakao')).then((credential) => {
      if (!credential) return null;

      return {
        idToken: credential.idToken,
        accessToken: credential.accessToken
      };
    });
  }

  return null;
}
